"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import SearchModalTrigger from "@/components/ui/SearchModalTrigger";
import { useAuthSession } from "@/components/providers/AuthSessionProvider";
import { useProfile } from "@/lib/hooks/useProfile";
import { getLocaleFromPathname } from "@/i18n/routing";
import { localePath } from "@/lib/locale-path";
import { NotificationBell } from "@/components/dashboard/notifications/NotificationBell";
import { HeaderNavDropdown } from "./HeaderNavDropdown";

export interface NavLink {
  key: string;
  label: string;
  href: string;
  description?: string;
  section?: "discover" | "compare";
}

export interface NavGroup {
  key: string;
  label: string;
  items: ReadonlyArray<NavLink>;
}

export type NavEntry = NavLink | NavGroup;

export function isNavGroup(entry: NavEntry): entry is NavGroup {
  return "items" in entry;
}

interface HeaderNavClientProps {
  entries: ReadonlyArray<NavEntry>;
}

/**
 * Active state ve mobile drawer state icin client island.
 * CTA buttons and mobile drawer are kept in this client island.
 */
export default function HeaderNavClient({ entries }: HeaderNavClientProps) {
  const pathname = usePathname() ?? "/";
  const locale = getLocaleFromPathname(pathname);
  const [open, setOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const { user, loading: authLoading } = useAuthSession();
  const authenticated = !authLoading && Boolean(user);
  const { data: profile } = useProfile({ enabled: authenticated });

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    drawerRef.current?.querySelector<HTMLElement>("a,summary,button")?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      toggleRef.current?.focus();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [open]);

  const isActive = (href: string): boolean => {
    const localPath = pathname.replace(/^\/(?:tr|en)(?=\/|$)/, "") || "/";
    if (href === "/") return localPath === "/";
    return localPath === href || localPath.startsWith(`${href}/`);
  };

  return (
    <>
      {/* Desktop nav */}
      <nav className="hidden lg:flex items-center gap-1">
        {entries.map((entry) =>
          isNavGroup(entry) ? (
            <HeaderNavDropdown key={entry.key} group={entry} isActive={isActive} />
          ) : (
            <Link
              key={entry.key}
              href={entry.href}
              prefetch={false}
              className={clsx(
                "relative px-3 py-5 text-[14px] font-medium transition-colors after:absolute after:inset-x-3 after:bottom-3 after:h-0.5 after:origin-center after:scale-x-0 after:bg-(--color-accent) after:transition-transform",
                isActive(entry.href)
                  ? "text-(--color-foreground) after:scale-x-100"
                  : "text-(--color-foreground) hover:text-(--color-brand)",
              )}
            >
              {entry.label}
            </Link>
          ),
        )}
      </nav>

      {/* CTA buttons */}
      <div className="hidden lg:flex items-center gap-3 shrink-0">
        {authenticated && user ? (
          <>
            <NotificationBell locale={locale} />
            <Link
              href={localePath(locale, "/hesabim")} prefetch={false}
              className="inline-flex h-10 items-center gap-2 rounded-xl border border-border px-3 text-[13px] font-medium text-foreground bg-bg-alt/50 hover:border-brand/40 transition-colors"
            >
              {profile?.avatar_url ? (
                <Image
                  src={profile.avatar_url}
                  alt=""
                  width={28}
                  height={28}
                  unoptimized
                  className="h-7 w-7 rounded-full object-cover shrink-0"
                />
              ) : (
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand/10 text-[11px] font-bold text-brand shrink-0">
                  {(user.full_name ?? user.email ?? "?").charAt(0).toUpperCase()}
                </span>
              )}
              {user.full_name?.split(" ")[0] ?? user.email ?? "Hesabım"}
            </Link>
            <Link href={localePath(locale, "/ilan-ver")} prefetch={false} className="h-10 px-5 inline-flex items-center justify-center rounded-xl bg-(--color-brand) text-(--color-brand-fg) text-[13px] font-bold hover:bg-(--color-brand-dark) transition-all active:scale-95 whitespace-nowrap">
              İlan Ver
            </Link>
          </>
        ) : (
          <>
            <Link
              href={localePath(locale, "/giris")} prefetch={false}
              className="h-10 px-5 inline-flex items-center justify-center rounded-xl border border-(--color-border) text-[13px] font-semibold text-(--color-foreground) hover:bg-(--color-bg-alt) hover:text-(--color-brand) transition-all active:scale-95 whitespace-nowrap"
            >
              Giriş Yap
            </Link>
            <Link
              href={localePath(locale, "/ilan-ver")} prefetch={false}
              className="h-10 px-5 inline-flex items-center justify-center rounded-xl bg-(--color-brand) text-(--color-brand-fg) text-[13px] font-bold hover:bg-(--color-brand-dark) hover:scale-[1.02] transition-all active:scale-95 shadow-lg shadow-(--color-brand)/20 whitespace-nowrap"
            >
              İlan Ver
            </Link>
          </>
        )}
      </div>

      <Link href={localePath(locale, authenticated ? "/hesabim" : "/giris")} prefetch={false}
        className="ml-auto hidden h-11 shrink-0 items-center justify-center rounded-lg border border-(--color-border) px-2 text-xs font-bold text-(--color-brand) min-[430px]:inline-flex lg:hidden">
        {authenticated ? "Hesabım" : "Giriş"}
      </Link>

      {/* Mobile toggle */}
      <button
        ref={toggleRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Menüyü aç/kapat"
        aria-expanded={open}
        className="inline-flex h-11 w-11 items-center justify-center rounded-[8px] bg-(--color-brand) text-(--color-brand-fg) shadow-sm transition-transform active:scale-95 lg:hidden"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.5">
          {open ? (
            <path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" />
          ) : (
            <path d="M4 6h12M4 10h12M4 14h12" strokeLinecap="round" />
          )}
        </svg>
      </button>

      {/* Mobile drawer */}
      {open && (
        <div ref={drawerRef} className="lg:hidden absolute left-0 right-0 top-full max-h-[calc(100dvh_-_5rem_-_env(safe-area-inset-bottom))] overflow-y-auto overscroll-contain border-t border-(--color-border) bg-(--color-surface) pb-[env(safe-area-inset-bottom)] shadow-lg">
          <nav aria-label="Mobil menü" className="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-3">
            <div className="grid grid-cols-2 gap-2 border-b border-(--color-border) pb-3 mb-2">
              <Link onClick={() => setOpen(false)} href={localePath(locale, authenticated ? "/hesabim" : "/giris")} className="flex min-h-11 items-center justify-center rounded-lg border border-(--color-brand) text-sm font-bold text-(--color-brand)">{authenticated ? "Hesabım" : "Giriş yap"}</Link>
              <Link onClick={() => setOpen(false)} href={localePath(locale, authenticated ? "/hesabim/ilanlarim" : "/kayit")} className="flex min-h-11 items-center justify-center rounded-lg border border-(--color-border) text-sm font-bold">{authenticated ? "İlanlarım" : "Üye ol"}</Link>
              <Link onClick={() => setOpen(false)} href={localePath(locale, "/ilan-ver")} className="col-span-2 flex min-h-11 items-center justify-center rounded-lg bg-(--color-brand) text-sm font-bold text-(--color-brand-fg)">+ İlan ver</Link>
            </div>
            {entries.map((entry) =>
              isNavGroup(entry) ? (
                <section key={entry.key} aria-labelledby={`mobile-nav-${entry.key}`} className="border-b border-(--color-border-soft) pb-3">
                  <div className="flex min-h-11 items-center justify-between gap-3 px-3">
                    <h2 id={`mobile-nav-${entry.key}`} className="text-sm font-bold text-(--color-foreground)">{entry.label}</h2>
                    <Link
                      href={localePath(locale, "/hal-fiyatlari")}
                      onClick={() => setOpen(false)}
                      prefetch={false}
                      className="text-xs font-semibold text-(--color-muted) underline decoration-(--color-border) underline-offset-4 hover:text-(--color-brand)"
                    >
                      Tüm fiyatlar
                    </Link>
                  </div>
                  {(["discover", "compare"] as const).map((section) => <div key={section} className="relative ml-3 border-l border-(--color-border) pl-3" aria-label={`${entry.label} ${section === "discover" ? "fiyat türleri" : "analiz araçları"}`}>
                    <span className="mb-1 mt-2 block px-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-(--color-muted)">{section === "discover" ? "Fiyat türleri" : "Analiz araçları"}</span>
                    {entry.items.filter((item) => item.section === section).map((item) => (
                      <Link
                        key={item.key}
                        href={localePath(locale, item.href)}
                        onClick={() => setOpen(false)}
                        prefetch={false}
                        aria-current={isActive(item.href) ? "page" : undefined}
                        className={clsx(
                          "relative flex min-h-11 items-center rounded-r-md px-3 py-2.5 text-sm font-medium transition-colors before:absolute before:-left-3 before:top-1/2 before:h-px before:w-3 before:bg-(--color-border)",
                          isActive(item.href)
                            ? "bg-(--color-brand-light) font-bold text-(--color-foreground) before:bg-(--color-accent)"
                            : "text-(--color-foreground) hover:bg-(--color-bg-alt)",
                        )}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>)}
                </section>
              ) : (
                <Link
                  key={entry.key}
                  href={localePath(locale, entry.href)}
                  onClick={() => setOpen(false)}
                  prefetch={false}
                  aria-current={isActive(entry.href) ? "page" : undefined}
                  className={clsx(
                    "min-h-11 px-3 py-3 rounded-md text-sm font-semibold",
                    isActive(entry.href)
                      ? "text-(--color-brand) bg-(--color-brand-light)"
                      : "text-(--color-foreground) hover:bg-(--color-bg-alt)",
                  )}
                >
                  {entry.label}
                </Link>
              ),
            )}
            {authenticated && user && (
              <Link
                href={localePath(locale, "/hesabim")} prefetch={false}
                className={clsx(
                  "px-3 py-3 rounded-md text-sm font-medium",
                  isActive("/hesabim")
                    ? "text-(--color-brand) bg-(--color-brand-light)"
                    : "text-(--color-foreground) hover:bg-(--color-bg-alt)",
                )}
              >
                Hesabım
              </Link>
            )}
          </nav>
        </div>
      )}

      <SearchModalTrigger />
    </>
  );
}
